import React, {useState, useEffect} from "react";

// Redux
import {useSelector, useDispatch} from "react-redux";
import {
  fetchGallery,
  editGallery,
  deleteGallery,
} from "../../redux/features/media/galleriesSlice";

// Routing
import {useParams, useHistory, Redirect} from "react-router-dom";

// Components
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Paper from "../UI/MainPaper";
import GalleriesForm from "./GalleriesForm";
import FullContainerProgress from "../UI/FullContainerProgress";
import DeleteButton from "../Buttons/DeleteButton";
import DeleteDialog from "../UI/DeleteDialog";
import BreadcrumbsWithEdit from "../Navigation/BreadcrumbsWithEdit";

const GalleriesEdit = () => {
  // Local State
  const [isEditMode, setEditMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Routing
  const {id} = useParams();
  const history = useHistory();

  // Redux
  const dispatch = useDispatch();
  const {currentPageGallery, isLoadingGallery, errorGallery} = useSelector(
    state => state.galleries
  );
  const {userPermission} = useSelector(state => {
    return state.auth;
  });
  const {write, delete: deletePermission} = userPermission.media;

  const formattedValues = {
    name: currentPageGallery.name,
    description: currentPageGallery.description,
    photos: currentPageGallery.photos,
  };

  let breadcrumbNameMap = {
    "/media": "Media",
    "/media/galleries": "Galleries",
  };
  breadcrumbNameMap[`/media/galleries/${id}`] = currentPageGallery.name;

  useEffect(() => {
    // check if the id is a int to keep going
    // This if is why javascript is broken :)
    if (Number.isInteger(id * 1) && id * 1 < Number.MAX_SAFE_INTEGER) {
      dispatch(fetchGallery(id));
    } else {
      history.push("/404");
    }
  }, [dispatch, history, id]);

  useEffect(() => {
    setEditMode(write);
  }, [write]);

  const onSubmit = async values => {
    const galleryObj = {
      id,
      ...values,
    };
    // Logic to show error or success
    const res = await dispatch(editGallery(galleryObj));
    if (!res.error) {
      history.push({
        pathname: "/media/galleries/",
        state: {
          showSuccess: true,
          snackbarContent: `Your changes to ${galleryObj.name} were saved successfully!`,
          snackbarLink: `/media/galleries/${galleryObj.id}`,
        },
      });
    }
  };

  const onDelete = async () => {
    // Logic to show error or success
    const res = await dispatch(deleteGallery(id));
    if (!res.error) {
      history.push({
        pathname: "/media/galleries",
        state: {
          showSuccess: true,
          snackbarContent: `The Gallery was successfully deleted!`,
        },
      });
    }
  };

  // Redirect if gallery doesn't exist'
  if (errorGallery && errorGallery.errorCode === 404) {
    return <Redirect to={"/404"} />;
  }

  return (
    <>
      <Grid container spacing={2}>
        <BreadcrumbsWithEdit
          isLoading={isLoadingGallery}
          breadcrumbNameMap={breadcrumbNameMap}
          isEditMode={isEditMode}
          setEditMode={setEditMode}
          writePermission={write}
        />

        <Grid item xs={12}>
          <Paper>
            {isLoadingGallery ? (
              <FullContainerProgress />
            ) : (
              <>
                <Typography component="h1" variant="h5" gutterBottom>
                  {currentPageGallery.name}
                </Typography>
                <GalleriesForm
                  defaultValues={formattedValues}
                  readOnly={!isEditMode}
                  submitButtonLabel="Save Changes"
                  onSubmit={onSubmit}
                  onCancel={() => setEditMode(false)}
                />
              </>
            )}
          </Paper>
        </Grid>
        {isEditMode && !isLoadingGallery && !!deletePermission && (
          <Grid item xs={12}>
            <DeleteButton onClick={() => setDialogOpen(true)} size="large">
              Delete Gallery
            </DeleteButton>
          </Grid>
        )}
      </Grid>
      {!!deletePermission && (
        <DeleteDialog
          dialogTitle="Are you sure you want to delete this Gallery?"
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={() => onDelete()}
        />
      )}
    </>
  );
};

export default GalleriesEdit;
